import {Component, ElementRef, Input, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {AppImageComponent} from '../image/image';
import {CarouselComponent} from '../carousel/carousel.component';
import {Photo} from '../../entities/photo/photo.entity';
import {ServicePhoto} from '../../entities/service/service-photo.entity';

@Component({
  selector: 'app-inline-carousel',
  standalone: true,
  imports: [
    CommonModule,
    AppImageComponent,
    CarouselComponent,
  ],
  templateUrl: './inline-carousel.component.html',
  styleUrls: ['./inline-carousel.component.scss'],
})
export class InlineCarouselComponent {
  @Input() photos: Photo[] | ServicePhoto[] | null = null;
  @Input() openFullScreenPhotoOnClick = true;
  @ViewChild('frame') frameRef?: ElementRef<HTMLElement>;

  public activeIndex = 0;
  public fullscreen = false;
  public dragging = false;
  private dragStartX: number|null = null;
  private dragOffset = 0;
  private capturedPointerTarget: HTMLElement|null = null;
  private readonly snapDragRatio = 0.6;

  public get hasPhotos(): boolean {
    return !!this.photos && this.photos.length > 0;
  }

  public prev(): void {
    const length = this.photos?.length ?? 0;
    if (!this.hasPhotos || length === 0) return;
    this.activeIndex = (this.activeIndex - 1 + length) % length;
    this.resetDrag();
  }

  public next(): void {
    const length = this.photos?.length ?? 0;
    if (!this.hasPhotos || length === 0) return;
    this.activeIndex = (this.activeIndex + 1) % length;
    this.resetDrag();
  }

  public openFullscreen(index?: number): void {
    if (!this.hasPhotos || !this.openFullScreenPhotoOnClick) return;
    if (typeof index === 'number') {
      this.activeIndex = index;
    }
    this.fullscreen = true;
  }

  public closeFullscreen(): void {
    this.fullscreen = false;
  }

  public onPointerDown(event: PointerEvent): void {
    if (!this.hasPhotos) return;
    if ((event.target as HTMLElement).closest('.inline-carousel__control')) return;
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragOffset = 0;
    const target = event.target as HTMLElement | null;
    target?.setPointerCapture?.(event.pointerId);
    this.capturedPointerTarget = target;
  }

  public onPointerMove(event: PointerEvent): void {
    if (!this.dragging || this.dragStartX === null) return;
    const rawOffset = event.clientX - this.dragStartX;
    this.dragOffset = this.clampDelta(rawOffset);
  }

  public onPointerUp(event: PointerEvent): void {
    if (!this.dragging || this.dragStartX === null) {
      this.resetDrag(event.pointerId);
      return;
    }

    const delta = this.clampDelta(event.clientX - this.dragStartX);
    const frameWidth = this.frameRef?.nativeElement.clientWidth ?? 0;
    const length = this.photos?.length ?? 0;
    if (frameWidth > 0 && length > 0) {
      const targetIndex = this.activeIndex - delta / (frameWidth * this.snapDragRatio);
      const snappedIndex = Math.round(targetIndex);
      const clampedIndex = Math.max(0, Math.min(length - 1, snappedIndex));
      this.activeIndex = clampedIndex;
    }

    this.resetDrag(event.pointerId);
  }

  public get trackTransform(): string {
    const offset = this.dragging ? `${this.dragOffset}px` : '0px';
    return `translateX(calc(-${this.activeIndex * 100}% + ${offset}))`;
  }

  private resetDrag(pointerId?: number): void {
    if (pointerId !== undefined && this.capturedPointerTarget) {
      this.capturedPointerTarget.releasePointerCapture?.(pointerId);
    }
    this.dragging = false;
    this.dragStartX = null;
    this.dragOffset = 0;
    this.capturedPointerTarget = null;
  }

  private clampDelta(delta: number): number {
    const length = this.photos?.length ?? 0;
    if (length === 0) return 0;
    if (this.activeIndex === 0 && delta > 0) return 0;
    if (length > 0 && this.activeIndex === length - 1 && delta < 0) return 0;
    return delta;
  }
}
